import { DeviceUsage2Illustration } from "./images/DeviceUsage2Illustration";
import {
  logViewJobResumeImprovementsCTAPressed,
  logViewJobResumeImprovementsCTAShown,
} from "../../../../../../interview-prep/logging/cta";
import { Box, Button, Flex, Heading, Text } from "../../../../../../mads";
import { LinearGradient } from "../../../../../../shared/components/LinearGradient/linear-gradient";
import { useI18n } from "../../../../../../shared/I18n";
import { JSTLandingScreen } from "../../../../../../shared/native-modules/tare/JSTLandingScreen";
import { logScreenViewWithExtraParams } from "../../../../../../shared/native-modules/tare/logScreenViewWithExtraParams";
import { logTapButtonWithExtraParams } from "../../../../../../shared/native-modules/tare/logTapButtonWithExtraParams";
import navigateTo from "../../../../../../shared/native-modules/tare/navigateTo";

import { borderRadius, colors, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useEffect } from "react";
import { StyleSheet } from "react-native";

const screenName = "rn-viewjob-resume-improvements-cta";
const tags = ["interviewPrep", "viewjob"];

/**
 * Resume Improvements entry-point CTA on View Job.
 *
 * After a job seeker completes an AI Mock Interview that generated resume
 * suggestions they haven't acted on, this banner surfaces a "Review
 * suggestions" CTA that deep-links into the resume-improvements review flow.
 * It is omitted when no interview was completed or no suggestions exist.
 *
 * Rendering is controlled by InterviewPrepEntryPoint, which queries
 * getJsInterviewPrepResumeImprovements and only mounts this component when
 * shouldShowCta is true.
 */
interface ResumeImprovementsCTAProps {
  /**
   * The extraction (pipeline) id whose improvements are being reviewed. Logged
   * with the banner's analytics events. Omitted when reviewing the latest
   * extraction.
   */
  extractionId?: string;
}

export const ResumeImprovementsCTA: React.FunctionComponent<ResumeImprovementsCTAProps> = ({
  extractionId,
}) => {
  const i18n = useI18n();

  useEffect(() => {
    void logViewJobResumeImprovementsCTAShown({
      extractionId,
    }).catch(console.error);
    void logScreenViewWithExtraParams(screenName, tags, {
      extractionId: extractionId ?? "",
    }).catch(console.error);
  }, [extractionId]);

  const handleReviewSuggestionsPress = () => {
    void navigateTo(JSTLandingScreen.resumeImprovementsReview(extractionId), "viewjob").catch(
      console.error,
    );

    void logViewJobResumeImprovementsCTAPressed({ extractionId }).catch(console.error);
    void logTapButtonWithExtraParams(screenName, "review_suggestions_button", tags, {
      extractionId: extractionId ?? "",
    }).catch(console.error);
  };

  return (
    <LinearGradient
      colors={[colors.utilityAiWeakStart, colors.utilityAiWeakEnd]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.container}
    >
      <Box sx={styles.content}>
        <Heading level={4}>{i18n.gettext("Make your resume richer")}</Heading>
        <Text level={2} sx={styles.body}>
          {i18n.gettext(
            "You mentioned a few things in your Mock Interview that aren't on your resume yet. We've made it easy to add them.",
          )}
        </Text>
        <Flex sx={styles.bottomContainer}>
          <Box sx={styles.buttonContainer}>
            <Button variant="secondary" size="md" onPress={handleReviewSuggestionsPress}>
              {i18n.gettext("Review suggestions")}
            </Button>
          </Box>
          <Box sx={styles.illustrationContainer}>
            <DeviceUsage2Illustration width={107} />
          </Box>
        </Flex>
      </Box>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    margin: spacing.s4,
    borderRadius: borderRadius.md,
  },
  content: {
    paddingHorizontal: 20,
    paddingVertical: spacing.s4,
  },
  body: {
    marginTop: spacing.s2,
    color: colors.foregroundStrong,
  },
  bottomContainer: {
    position: "relative",
    marginTop: spacing.s5,
  },
  buttonContainer: {
    maxWidth: 220,
    zIndex: 1,
  },
  illustrationContainer: {
    position: "absolute",
    right: -2,
    bottom: -spacing.s4,
  },
});
